import React, { useState } from 'react';
import { Lead } from '../types';
import { leadsService } from '../services/api';

interface SaleConfirmationModalProps {
  lead: Lead;
  onClose: () => void;
  onConfirm: () => void;
}

const SaleConfirmationModal: React.FC<SaleConfirmationModalProps> = ({ lead, onClose, onConfirm }) => {
  const [dataVenda, setDataVenda] = useState(() => {
    // Se já tem data da venda, usa ela; senão usa hoje
    if (lead.data_venda) {
      return lead.data_venda.split('T')[0]; // Remove a parte de tempo se houver
    }
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!dataVenda) {
      setError('Selecione a data da venda');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await leadsService.updateLeadComprou(lead.id, true, dataVenda);
      onConfirm();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao confirmar venda');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSale = async () => {
    setLoading(true);
    setError('');

    try {
      await leadsService.updateLeadComprou(lead.id, false);
      onConfirm();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao remover venda');
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysToConversion = () => {
    if (!dataVenda || !lead.data_importacao) return null;
    
    const dataImportacao = new Date(lead.data_importacao);
    const dataVendaDate = new Date(dataVenda);
    const diffTime = Math.abs(dataVendaDate.getTime() - dataImportacao.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const diasParaConversao = calculateDaysToConversion();

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {lead.comprou ? '✏️ Editar Venda' : '💰 Confirmar Venda'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Lead:</h4>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm font-medium text-gray-800">{lead.reu_nome || 'Nome não informado'}</p>
              <p className="text-xs text-gray-600">{lead.numero_processo}</p>
              {lead.data_importacao && (
                <p className="text-xs text-gray-500">
                  Lead importada em: {new Date(lead.data_importacao).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📅 Data da Venda *
            </label>
            <input
              type="date"
              value={dataVenda}
              onChange={(e) => setDataVenda(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          {diasParaConversao !== null && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>⏱️ Tempo de conversão:</strong> {diasParaConversao} {diasParaConversao === 1 ? 'dia' : 'dias'}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {diasParaConversao <= 7 && '🚀 Conversão rápida!'}
                {diasParaConversao > 7 && diasParaConversao <= 30 && '⭐ Boa conversão!'}
                {diasParaConversao > 30 && '📈 Conversão longa'}
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            {lead.comprou && (
              <button
                onClick={handleRemoveSale}
                disabled={loading}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Removendo...' : '✗ Remover Venda'}
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={loading || !dataVenda}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? (lead.comprou ? 'Salvando...' : 'Confirmando...') : (lead.comprou ? '💾 Salvar Alterações' : '✓ Confirmar Venda')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleConfirmationModal;